/**
 * @author        calvin@zumper.com (Calvin Yau)
 * @copyright     Copyright (c) 2018, Zumper
 * @description   Functions to retrurn datetimes into text like for displaying viewings
 */

import { isNumber } from '@zcode/utils'
import { format, isValid, parse } from 'date-fns'

/**
 * Get text for a specific viewing.
 *
 * @param startTimestamp time string in epoch form
 * @param endTimestamp time string in epoch form
 * @param timeZone to display time in, like `America/Los_Angeles`
 */
const getDateTimeTextInfo = (
  startTimestamp: number,
  endTimestamp: number,
  timeZone: string | null | undefined
) => {
  const startDateTime = new Date(startTimestamp * 1000)
  const endDateTime = new Date(endTimestamp * 1000)

  if (timeZone) {
    let startString: string
    let endString: string
    let endStringTz: string
    try {
      startString = startDateTime.toLocaleString('en-US', { timeZone })
      endString = endDateTime.toLocaleString('en-US', { timeZone })
      endStringTz = endDateTime.toLocaleString('en-US', { timeZone, timeZoneName: 'short' })
    } catch (e) {
      return getLocalTimeVersion(startDateTime, endDateTime)
    }

    const start = parse(startString, 'M/d/yyyy, h:mm:ss aa', new Date())
    const end = parse(endString, 'M/d/yyyy, h:mm:ss aa', new Date())
    const match = endStringTz.match(/\s\w+$/)
    const tz = match ? match[0] : ''

    return { start, end, tz }
  }
  return getLocalTimeVersion(startDateTime, endDateTime)
}

export const getShortDateTimeText = (
  startTimestamp: number,
  endTimestamp: number,
  timeZone: string | null | undefined
) => {
  if (!isNumber(startTimestamp) || !isNumber(endTimestamp)) {
    return ''
  }

  const { start, end, tz } = getDateTimeTextInfo(startTimestamp, endTimestamp, timeZone)
  if (isValid(start) && isValid(end)) {
    // short text ex: Wed Mar 14 11a - 12p PDT
    const shortStartFormat = start.getMinutes() === 0 ? 'E MMM d ha' : 'EEE MMM d h:mma'
    const shortEndFormat = end.getMinutes() === 0 ? 'ha' : 'h:mma'
    // change 'am' and 'pm' to 'a' and 'p' respectively
    const shortStartText = format(start, shortStartFormat).replace('AM', 'a').replace('PM', 'p')
    const shortEndText = format(end, shortEndFormat).replace('AM', 'a').replace('PM', 'p')
    return `${shortStartText} \u2013 ${shortEndText}${tz}` as const
  }
  return ''
}

export const getLongDateTimeText = (
  startTimestamp: number,
  endTimestamp: number,
  timeZone: string | null | undefined
) => {
  if (!isNumber(startTimestamp) || !isNumber(endTimestamp)) {
    return ''
  }

  const { start, end, tz } = getDateTimeTextInfo(startTimestamp, endTimestamp, timeZone)
  if (isValid(start) && isValid(end)) {
    // long text ex: Wednesday, March 14 @ 11:00am - 12:00pm PDT
    const longStartText = format(start, 'EEEE, MMMM d @ h:mma')
    const longEndText = format(end, 'h:mma')
    return `${longStartText} \u2013 ${longEndText}${tz}` as const
  }
  return ''
}

const getLocalTimeVersion = (startDateTime: Date, endDateTime: Date) => {
  return { start: startDateTime, end: endDateTime, tz: ' (your local time)' } as const
}
